import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { PlayCircle, CheckCircle2, Loader2, DollarSign, ListChecks, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from '../ui/sonner';

/**
 * Homepage callout that publicizes the one-click demo. Sits between the
 * feature grid and the final CTA — the position on the page where a
 * skeptical prospect is most likely to want "prove it to me" evidence
 * before committing to sign up.
 */
const DemoCalloutSection = () => {
  const { demoLogin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const startDemo = async () => {
    setLoading(true);
    try {
      const { estate_id } = await demoLogin();
      toast.success('Welcome! You are exploring the demo estate.');
      navigate(estate_id ? `/estates/${estate_id}` : '/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start the demo.');
      setLoading(false);
    }
  };

  const highlights = [
    { icon: DollarSign, title: '$2.4M pre-loaded estate', body: 'Nine real-world assets across bank, brokerage, retirement, real estate, life insurance, vehicles, and personal property.' },
    { icon: ListChecks, title: '11 executor tasks, mid-flight', body: 'Some completed, some in progress, some pending — see how state deadlines, priorities, and status states look together.' },
    { icon: Users, title: '3 heirs with real distributions', body: 'Percentage-based residuary + a specific bequest so you can see how the distribution math falls out.' },
  ];

  return (
    <section className="py-24 bg-ep-subtle" data-testid="demo-callout-section">
      <div className="container-custom">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-2">
            <span className="inline-block text-xs font-semibold tracking-wider uppercase text-ep-primary bg-ep-primary/10 px-3 py-1 rounded-full mb-4">
              See it in action
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ep-text mb-4 leading-tight">
              Skip the signup —{' '}
              <span className="italic text-ep-primary">tour a real estate</span>{' '}
              in one click.
            </h2>
            <p className="text-ep-text-secondary mb-6 text-base leading-relaxed">
              We've pre-populated a sample estate with everything you'd manage in real life —
              assets, tasks, cashflow, heirs, court filings, and executor time logs.
              Click the button and you're in. No credit card. No account. No email.
            </p>
            <Button
              onClick={startDemo}
              disabled={loading}
              className="bg-ep-primary text-white hover:bg-ep-primary-dark px-8 py-6 text-base font-medium"
              data-testid="demo-callout-cta"
            >
              {loading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading demo…</>
                : <><PlayCircle className="w-4 h-4 mr-2" /> Try the Live Demo</>}
            </Button>
            <p className="text-xs text-ep-text-muted mt-3">
              The dataset resets on every demo login so every visitor sees the same pristine sandbox.
            </p>
          </div>

          <div className="lg:col-span-3 grid sm:grid-cols-3 gap-4">
            {highlights.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="bg-white rounded-2xl border border-ep-border/50 p-5 shadow-soft flex flex-col"
                data-testid={`demo-callout-tile-${title.split(' ')[0].toLowerCase()}`}
              >
                <div className="w-10 h-10 rounded-lg bg-ep-primary/10 text-ep-primary flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-ep-text mb-1.5 leading-snug">
                  {title}
                </h3>
                <p className="text-sm text-ep-text-muted leading-relaxed">{body}</p>
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-auto pt-3" strokeWidth={2} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoCalloutSection;
